'use client';

import { useMemo, useState } from 'react';
import {
  Button,
  Chip,
  NumberPicker,
  SkeletonBlock,
  SkeletonText,
} from '@galapagos/design-system/web';
import { typography } from '@galapagos/design-system/typography';
import {
  binaryReportedWeekAnswers,
  countedReportedWeekAnswers,
  reportedWeekAnswers,
  type BinaryDayChoice,
} from '@galapagos/shared';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { baselineAnchor, reportedDays } from '../../baseline/baseline-days';
import { MAX_DAY_COUNT } from '../../baseline/baseline-week';
import {
  baselinePassThroughStep,
  useBaselineSave,
  useBaselineWeekReads,
} from '../../baseline/useBaselineStep';
import { FormActions } from '../../components/FormActions';
import { PrivateFields } from '../../components/PrivateFields';
import { UnitGuideCallout } from '../../components/UnitGuide';
import { YesNoDayRow } from '../../components/YesNoDayRow';
import { useStepHeading } from '../../hooks/useStepHeading';
import { memberTimeZone } from '../../wrap-up/booking-slots';
import { gated } from '../wrap-up/gated';
import { BaselineFallbackActions, BaselineFrame, useBaselineStatus } from './BaselineFrame';

/**
 * Placeholder rows while the step's reads are in flight. Whether the program
 * counts or asks yes/no is only known once they land, so each row is a bounded
 * estimate: the yes/no day row's height (its day label and date lines inside
 * the row's own vertical padding), one silhouette per reported day. The loaded
 * rows are hairline-divided rather than rounded cards, so the corners use the
 * smallest radius token instead of the primitive default. Both loaded forms
 * open with a description under the title, so its two lines are held first.
 */
const SKELETON_ROW_HEIGHT = typography.b2Bold.lineHeight + typography.c2Regular.lineHeight;

function BaselineWeekSkeleton({ rows }: { rows: number }) {
  return (
    <div aria-hidden="true" data-testid="signup-baseline-week-skeleton">
      <SkeletonText variant="b2Regular" lines={2} />
      <ul className="signup-baseline__days signup-baseline__skeleton">
        {Array.from({ length: rows }, (_, index) => (
          <li key={index} className="signup-baseline__skeleton-row">
            <SkeletonBlock height={SKELETON_ROW_HEIGHT} radius="s" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * `/baseline-week/`: the member's count for each of the last seven
 * days in their own zone, most recent first, saved to the intake session. A
 * program that captures yes/no asks whether they used each day instead.
 */
function BaselineWeekStep() {
  const { t, i18n } = useTranslation('signup');
  const language = i18n.language;
  const reads = useBaselineWeekReads();
  const title =
    (reads.status === 'ready' && reads.data.capture.mode === 'yes-no'
      ? reads.data.wording.weekTitle
      : undefined) ?? t('baseline.week.title');
  const headingRef = useStepHeading(title);
  const zone = useMemo(() => memberTimeZone(), []);
  // Fixed for the life of the step: a member who crosses midnight here still
  // saves the week they were shown.
  const [anchorDate] = useState(() => baselineAnchor(DateTime.now(), zone));
  const days = useMemo(
    () => reportedDays(anchorDate, zone, language),
    [anchorDate, zone, language],
  );
  const [counts, setCounts] = useState<number[]>(() => days.map(() => 0));
  // Unanswered until pressed: a day left unanswered is saved as No.
  const [choices, setChoices] = useState<BinaryDayChoice[]>(() => days.map(() => undefined));
  const save = useBaselineSave();
  const sessionRead = reads.status === 'ready' ? reads.data.session : undefined;
  // The table is ready before the session is: only a failed session read is
  // announced, since a busy Continue already says the save is not ready yet.
  const { status, statusTone } = useBaselineStatus(
    sessionRead?.status === 'error' ? sessionRead : reads,
    save,
  );

  if (reads.status !== 'ready') {
    return (
      <BaselineFrame
        title={title}
        headingRef={headingRef}
        status={status}
        statusTone={statusTone}
        body={reads.status === 'loading' ? <BaselineWeekSkeleton rows={days.length} /> : undefined}
      >
        <BaselineFallbackActions
          read={reads}
          passThroughTo={baselinePassThroughStep('baseline-week')}
        />
      </BaselineFrame>
    );
  }

  const { wording, capture, session } = reads.data;
  const choose = (index: number, next: 0 | 1) =>
    setChoices((previous) => previous.map((choice, at) => (at === index ? next : choice)));
  const { unitGuide } = wording;
  const yesNoBody = (
    /* Whether a member used each day is health information: no autocapture or RUM click here. */
    <PrivateFields>
      <ul className="signup-baseline__days" aria-label={t('baseline.week.daysLabel')}>
        {days.map((day, index) => {
          const label = day.ordinal === 1 ? t('baseline.week.yesterday') : day.weekdayLabel;
          return (
            <li key={day.date} className="signup-baseline__day">
              <YesNoDayRow
                label={label}
                subtitle={day.shortDate}
                data-testid={`signup-baseline-week-day-${day.ordinal}`}
              >
                {/* The save sends the answers as they are when Continue is
                    pressed, so they cannot change until it settles. */}
                <Chip
                  size="L"
                  label={t('baseline.yesNo.no')}
                  selected={choices[index] === 0}
                  disabled={save.pending}
                  actionId="signup.baseline-week-day-no.activate"
                  onPress={() => choose(index, 0)}
                  data-testid={`signup-baseline-week-day-${day.ordinal}-no`}
                />
                <Chip
                  size="L"
                  label={t('baseline.yesNo.yes')}
                  selected={choices[index] === 1}
                  disabled={save.pending}
                  actionId="signup.baseline-week-day-yes.activate"
                  onPress={() => choose(index, 1)}
                  data-testid={`signup-baseline-week-day-${day.ordinal}-yes`}
                />
              </YesNoDayRow>
            </li>
          );
        })}
      </ul>
    </PrivateFields>
  );
  const counterBody = (
    <>
      {unitGuide ? (
        <UnitGuideCallout guide={unitGuide} data-testid="signup-baseline-week-guide-callout" />
      ) : null}
      <p className="signup-baseline__limit" data-testid="signup-baseline-week-limit">
        {t('baseline.limit', { max: MAX_DAY_COUNT })}
      </p>
      {/* A member's daily counts are health information: no autocapture or RUM click here. */}
      <PrivateFields>
        <ul className="signup-baseline__days" aria-label={t('baseline.week.daysLabel')}>
          {days.map((day, index) => {
            const label = day.ordinal === 1 ? t('baseline.week.yesterday') : day.weekdayLabel;
            const testId = `signup-baseline-week-day-${day.ordinal}`;
            return (
              <li key={day.date} className="signup-baseline__day">
                <NumberPicker
                  label={label}
                  subtitle={day.shortDate}
                  value={counts[index] ?? 0}
                  min={0}
                  max={MAX_DAY_COUNT}
                  layout="inline"
                  // The save sends the counts as they are when Continue is
                  // pressed, so they cannot change until it settles.
                  disabled={save.pending}
                  actionId="signup.baseline-week-day.change"
                  decrementActionId="signup.baseline-week-day.decrement"
                  incrementActionId="signup.baseline-week-day.increment"
                  decrementAccessibilityLabel={t('baseline.fewer', { day: label })}
                  incrementAccessibilityLabel={t('baseline.more', { day: label })}
                  onChange={(next) =>
                    setCounts((previous) =>
                      previous.map((count, at) => (at === index ? next : count)),
                    )
                  }
                  data-testid={testId}
                  decrementTestID={`${testId}-decrement`}
                  valueTestID={`${testId}-value`}
                  incrementTestID={`${testId}-increment`}
                />
              </li>
            );
          })}
        </ul>
      </PrivateFields>
    </>
  );

  return (
    <BaselineFrame
      title={title}
      description={wording.weekDescription}
      headingRef={headingRef}
      status={status}
      statusTone={statusTone}
      body={capture.mode === 'yes-no' ? yesNoBody : counterBody}
    >
      {session.status === 'ready' || session.status === 'loading' ? (
        <FormActions>
          {/* The save merges into the freshly read answers, so it waits for
              them; a press before they land is dropped, not queued. */}
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            state={save.pending || session.status === 'loading' ? 'Loading' : 'Default'}
            actionId="signup.baseline-week-continue.activate"
            data-testid="signup-baseline-week-continue-button"
            onPress={() => {
              if (session.status !== 'ready') return;
              const answers = session.data;
              save.save(
                capture.mode === 'yes-no'
                  ? binaryReportedWeekAnswers({
                      anchorDate,
                      choices,
                      program: capture.program,
                      answers,
                    })
                  : capture.program
                    ? countedReportedWeekAnswers({
                        anchorDate,
                        counts,
                        program: capture.program,
                        answers,
                      })
                    : reportedWeekAnswers({ anchorDate, counts }),
                'baseline-typical',
              );
            }}
          />
        </FormActions>
      ) : (
        <BaselineFallbackActions
          read={session}
          passThroughTo={baselinePassThroughStep('baseline-week')}
        />
      )}
    </BaselineFrame>
  );
}

export const BaselineWeekScreen = gated(BaselineWeekStep, 'baseline-week');
